import posthog from 'posthog-js';
import { useAuthStore } from '@/stores/auth';

// PostHog is only active when the key is set at build time. Local dev .env
// leaves it empty so dev sessions are not recorded; the production value
// lives in the server-managed .env on the prod host.
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;

let initialized = false;

export function initPostHog(): void {
  if (!POSTHOG_KEY) return;

  posthog.init(POSTHOG_KEY, {
    // Reverse-proxied through our own nginx (/ph) so adblockers don't drop
    // events and the CSP can stay 'self'-only.
    api_host: `${window.location.origin}/ph`,
    ui_host: 'https://eu.posthog.com',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    session_recording: {
      // Payroll/crypto app — never record what users type
      maskAllInputs: true,
    },
  });
  initialized = true;

  bindUserIdentity();
}

// Tie PostHog sessions to the logged-in user. Subscribing to the auth
// store covers every path that changes the user: email login, register,
// Google OAuth callback, session restore on refresh, and logout.
function bindUserIdentity(): void {
  let identifiedUserId: string | null = null;

  useAuthStore.subscribe((state) => {
    const userId = state.user?.id ?? null;
    if (userId === identifiedUserId) return;
    identifiedUserId = userId;

    if (userId) {
      posthog.identify(userId, {
        email: state.user?.email,
        name: state.user?.fullName,
        role: state.user?.role,
      });
    } else {
      posthog.reset();
    }
  });
}

// Safe capture wrapper — no-op when PostHog is disabled (dev builds).
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}
